<template>
    <page-wrapper>
        <page-header>
            <h1>{{ $tc('common.term.dashboard', 1) }} <span class="subtitle">{{ $store.state.user.getUserName() }}</span></h1>
        </page-header>

        <!--region # Misc/General-->
        <div class="row print-row-md q-col-gutter-lg">
            <!--region ## Contract Expiration List-->
            <grid-card
                :columns="{ xs: 12, sm: 12, md: 6, lg: 7, xl: 7 }"
                :heading="$t('common.reporting.global-report.contracts-expiration-report.title')"
                :heading-level="3"
                :subheading="!contractsExpirationReportIsLoading ? $tc('common.reporting.global-report.contracts-expiration-report.n-contracts-with-end-date-in-period', filteredContractsExpirationReportData?.length ?? 0) : `${$t('common.term.loading')} …`"
                :heading-info="`<p>${$t('common.reporting.global-report.contracts-expiration-report.info')}</p><p>${$tc('common.reporting.global-report.data-updated-n-times-daily', 1)}${ filteredContractsExpirationReportData ? ` (${$tc('common.reporting.global-report.generated-on--date-time--list', 1, { date: formatDate(contractsExpirationReport?.updated_at), time: formatTime(contractsExpirationReport?.updated_at) })})` : ''}</p>`"
                min-height="30em"
                max-height="40em"
                content-scrollable
                :no-content-padding="contractsExpirationReport && contractsExpirationReport?.data?.length > 0 ? true : null"
                :loading="contractsExpirationReportIsLoading"
                header-border
                footer-border
                :footer-styles="{ minHeight: '59px'}"
            >
                <q-list
                    v-if="filteredContractsExpirationReportData?.length"
                    class="expiring-contracts-list"
                    separator
                >
                    <q-item
                        v-for="contractExpiration in filteredContractsExpirationReportData"
                        :key="`contract-expiration-${contractExpiration.id}`"
                        :to="{ name: 'contract-detail', params: { contactId: contractExpiration.customer_id.toString(), id: contractExpiration.id.toString() } }"
                    >
                        <q-item-section avatar>
                            <div class="text-center self-center" style="width: 65px;">
                                <div>{{ formatMonthDay(contractExpiration.end_date) }}</div>
                                <small
                                    v-if="daysUntilExpiration(contractExpiration.end_date) <= 60"
                                    class="text-secondary"
                                >{{ $tc(`common.time.${daysUntilExpiration(contractExpiration.end_date) >= 0 ? 'in-n-days' : 'n-days-ago'}--text`, daysUntilExpiration(contractExpiration.end_date), { 'n': Math.abs(daysUntilExpiration(contractExpiration.end_date)) }) }}</small>
                            </div>
                        </q-item-section>

                        <q-item-section>
                            <p class="q-mb-none">{{ contractExpiration.number }} <span class="text-secondary">– {{ contractExpiration.customer_name }}</span></p>
                            <p class="text-secondary"><small>{{ contractExpiration.product_name }} – {{ contractExpiration.product_provider_name }}</small></p>
                        </q-item-section>

                        <q-item-section side>
                            <div class="flex q-gutter-x-sm">
                                <status-badge
                                    v-if="contractExpiration.cancellation"
                                    :status="`cancellation-${kebabCase(contractExpiration.cancellation)}`"
                                    info-icon-color="warning-dark"
                                    :info-title="$tc('common.contract.cancelled-contract', 1)"
                                    :info-text="[
                                        $tc('common.contract.cancellation-info--contract-cancelled-per', 1, { cancellationDate: formatDate(contractExpiration.cancellation_date) }),
                                        $tc(`common.contract.cancellation-info--status-${contractExpiration.cancellation.toLowerCase()}`, 1)
                                    ].join(' ')"
                                />
                                <info-icon
                                    v-if="contractExpiration.auto_renewal"
                                    name="mib-common-file-sync"
                                    size="1em"
                                    :title="$t('forms.field.label.contract.automatic-renewal')"
                                    :text="$tc('common.reporting.global-report.contracts-expiration-report.contract-automatically-renewed-for-n-months', contractExpiration.auto_renewal || 0)"
                                />
                            </div>
                        </q-item-section>
                    </q-item>
                </q-list>
                <p v-else class="visually-hidden">{{ $tc('common.reporting.global-report.contracts-expiration-report.n-contracts-with-end-date-in-period', 0) }}</p>

                <template v-slot:actions>
                    <div class="q-gutter-md">
                        <q-checkbox
                            v-model="contractsExpirationReportShowAutoRenewal"
                            :label="$t('common.reporting.global-report.contracts-expiration-report.show-auto-renewal')"
                            dense
                        />
                        <q-checkbox
                            v-model="contractsExpirationReportShowCancellation"
                            :label="$t('common.reporting.global-report.contracts-expiration-report.show-cancelled-contracts')"
                            dense
                        />
                    </div>
                </template>
            </grid-card>
            <!--endregion-->

            <!--region ## Birthday List-->
            <grid-card
                :columns="{ xs: 12, sm: 12, md: 6, lg: 5, xl: 5 }"
                :heading="$t('common.reporting.global-report.contacts-birthday-report.title')"
                :heading-level="3"
                :subheading="!contactsBirthdayReportIsLoading ? $tc('common.reporting.global-report.contacts-birthday-report.n-birthdays-in-period', contactsBirthdayReport?.data?.length ?? 0) : `${$t('common.term.loading')} …`"
                :heading-info="`<p>${$t('common.reporting.global-report.contacts-birthday-report.info')}</p><p>${$tc('common.reporting.global-report.data-updated-n-times-daily', 1)}${ contactsBirthdayReport ? ` (${$tc('common.reporting.global-report.generated-on--date-time--list', 1, { date: formatDate(contactsBirthdayReport?.updated_at), time: formatTime(contactsBirthdayReport?.updated_at) })})` : '' }</p>`"
                :min-height="$q.screen.gt.md ? '30em' : '20em'"
                max-height="40em"
                content-scrollable
                :no-content-padding="contactsBirthdayReport && contactsBirthdayReport?.data?.length > 0 ? true : null"
                :loading="contactsBirthdayReportIsLoading"
                header-border
                footer-border
            >
                <q-list
                    v-if="contactsBirthdayReport?.data?.length"
                    class="birthday-list"
                    separator
                >
                    <q-item
                        v-for="contactBirthday in contactsBirthdayReport.data"
                        :key="`contact-birthday-${contactBirthday.id}`"
                        :to="{ name: 'contact-detail', params: { id: contactBirthday.id.toString() } }"
                        :class="[{ 'today bg-warning-lighter': daysUntilBirthday(contactBirthday.date_of_birth) === 0 }]"
                    >
                        <q-item-section avatar>
                            <div :class="['text-center self-center', { 'text-bold': daysUntilBirthday(contactBirthday.date_of_birth) === 0 }]" style="width: 65px;">
                                <div>{{ formatMonthDay(contactBirthday.date_of_birth) }}</div>
                                <small
                                    v-if="daysUntilBirthday(contactBirthday.date_of_birth) <= 14"
                                    class="text-secondary"
                                >{{ $tc(`common.time.${daysUntilBirthday(contactBirthday.date_of_birth) >= 0 ? 'in-n-days' : 'n-days-ago'}--text`, daysUntilBirthday(contactBirthday.date_of_birth), { 'n': Math.abs(daysUntilBirthday(contactBirthday.date_of_birth)) }) }}</small>
                            </div>
                        </q-item-section>

                        <q-item-section>
                            <p><span :class="[{ 'text-bold': daysUntilBirthday(contactBirthday.date_of_birth) === 0 }]">{{ contactBirthday.name }}</span> <span class="text-secondary">({{ formatDate(contactBirthday.date_of_birth) }} – <span>{{ age(contactBirthday.date_of_birth) }}&nbsp;{{ $tc('common.time.year', age(contactBirthday.date_of_birth)) }}</span>)</span></p>
                        </q-item-section>

                        <q-item-section side>
                            <q-icon
                                v-if="daysUntilBirthday(contactBirthday.date_of_birth) === 0"
                                name="mib-cake-birthday"
                                size="1em"
                                color="yellow-10"
                            />
                        </q-item-section>
                        <q-tooltip
                            :delay="1000"
                            anchor="bottom middle"
                            self="center middle"
                            :offset="[0, 5]"
                        >{{ birthdayTooltipText(contactBirthday.name, contactBirthday.date_of_birth) }}</q-tooltip>
                    </q-item>
                </q-list>
            </grid-card>
            <!--endregion-->
        </div>
        <!--endregion-->

        <!--region # Tasks-->
        <div v-if="$can('Feature:tasks:core') && ['Task:manage:own', 'Task:manage:all'].some(permission => $can(permission))">
            <div class="row q-col-gutter-md q-mt-md">
                <h2>{{ $tc('common.task.task', 2) }}</h2>
            </div>

            <div class="row print-row-md q-col-gutter-lg">
                <!-- TODO improvement: Card settings (user settings) for how many tasks should be shown in this card. -->
                <grid-card
                    :columns="{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }"
                    :heading="$t('common.task.assigned-to-me')"
                    :heading-level="3"
                    :subheading="tasksAssignedToUser ? $tc('common.task.task--n-tasks', numberOfTasks.assignedToUser) : null"
                    min-height="30em"
                    max-height="40em"
                    content-scrollable
                    :no-content-padding="tasksAssignedToUser && numberOfTasks.assignedToUser > 0 ? true : null"
                    :loading="!tasksAssignedToUser"
                    header-border
                    footer-border
                    :footer-styles="{ minHeight: '59px'}"
                >
                    <template v-if="tasksAssignedToUser">
                        <template v-if="numberOfTasks.assignedToUser > 0">
                            <task-info-list
                                :tasks="tasksAssignedToUser"
                                group="tasks-assigned-to-me"
                                no-assignee
                            />

                            <p v-if="numberOfTasks.assignedToUser > numberOfTasks.assignedToUserShown" class="additional-info q-pa-md">
                                {{ $tc('common.task.only-first-n-tasks-shown', numberOfTasks.assignedToUserShown) }}
                            </p>
                        </template>
                    </template>

                    <template v-slot:actions>
                        <base-button
                            :label="$t('common.task.all-my-assigned-tasks')"
                            :to="{ name: 'task-list', query: { filterAssigneeId: userConsultantId } }"
                            primary-button
                            flat
                        />
                    </template>
                </grid-card>

                <grid-card
                    :columns="{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }"
                    :heading="$t('common.task.created-by-me')"
                    :heading-level="3"
                    :subheading="tasksCreatedByUser ? $tc('common.task.task--n-tasks', numberOfTasks.createdByUser) : null"
                    min-height="30em"
                    max-height="40em"
                    :loading="!tasksCreatedByUser"
                    content-scrollable
                    :no-content-padding="tasksCreatedByUser && numberOfTasks.createdByUser > 0 ? true : null"
                    header-border
                    footer-border
                    :footer-styles="{ minHeight: '59px'}"
                >
                    <template v-slot:headerMenu>
                        <q-list separator>
                            <q-item clickable @click="() => triggerCreateTask()">
                                <q-item-section>{{ $tc('common.task.add-task', 1) }}</q-item-section>
                            </q-item>
                        </q-list>
                    </template>

                    <template v-if="tasksCreatedByUser">
                        <template v-if="numberOfTasks.createdByUser > 0">
                            <task-info-list
                                :tasks="tasksCreatedByUser"
                                group="tasks-created-by-me"
                                no-creator
                            />

                            <p v-if="numberOfTasks.createdByUser > numberOfTasks.createdByUserShown" class="additional-info q-pa-md">
                                {{ $tc('common.task.only-first-n-tasks-shown', numberOfTasks.createdByUserShown) }}
                            </p>
                        </template>
                    </template>

                    <template v-slot:actions>
                        <base-button
                            :label="$t('common.task.all-my-created-tasks')"
                            :to="{ name: 'task-list', query: { filterCreatorId: userConsultantId } }"
                            primary-button
                            flat
                        />
                    </template>
                </grid-card>

                <!-- TODO improvement @MTR: Use D3.js pie chart (or similar) -->
                <grid-card
                    :columns="{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }"
                    :heading="$tc('views.dashboard.tasks.statistic', 1)"
                    :heading-level="3"
                    :heading-info="$t('views.dashboard.tasks.statistic--description')"
                    :loading="!tasksLoaded"
                >
                    <template v-if="tasksLoaded">
                        <template v-if="numberOfTasks.pending">
                            <p>{{ $tc('views.dashboard.tasks.there-are-n-pending-tasks', numberOfTasks.pending) }}:</p>

                            <q-linear-progress
                                size="24px"
                                :value="numberOfTasks.status[TaskStatus.TODO] / numberOfTasks.pending"
                                color="grey-5"
                                class="q-mb-sm"
                            >
                                <div class="absolute-full flex flex-center">
                                    <q-badge
                                        color="white"
                                        text-color="secondary-dark"
                                        :label="`${$t('common.status.todo')}: ${numberOfTasks.status[TaskStatus.TODO]} (${((100 / numberOfTasks.pending) * numberOfTasks.status[TaskStatus.TODO]).toFixed(0)}%)`"
                                    />
                                </div>
                            </q-linear-progress>

                            <q-linear-progress
                                size="24px"
                                :value="numberOfTasks.status[TaskStatus.IN_PROGRESS] / numberOfTasks.pending"
                                color="lime"
                                class="q-mb-sm"
                            >
                                <div class="absolute-full flex flex-center">
                                    <q-badge
                                        color="white"
                                        text-color="secondary-dark"
                                        :label="`${$t('common.status.in-progress')}: ${numberOfTasks.status[TaskStatus.IN_PROGRESS]} (${((100 / numberOfTasks.pending) * numberOfTasks.status[TaskStatus.IN_PROGRESS]).toFixed(0)}%)`"
                                    />
                                </div>
                            </q-linear-progress>
                        </template>
                        <template v-else>
                            <p>{{ $tc('views.dashboard.tasks.no-pending-tasks-text', (numberOfTasks.status[TaskStatus.DONE] > 20) ? 0 : numberOfTasks.status[TaskStatus.DONE]) }}</p>

                            <template v-if="numberOfTasks.status[TaskStatus.DONE]">
                                <q-linear-progress
                                    size="24px"
                                    :value="0"
                                    color="grey-5"
                                    class="q-mb-sm"
                                >
                                    <div class="absolute-full flex flex-center">
                                        <q-badge
                                            color="white"
                                            text-color="secondary-dark"
                                            :label="`${$tc('common.task.task--pending-task--n-pending-tasks', 0)} (0%)`"
                                        />
                                    </div>
                                </q-linear-progress>

                                <q-linear-progress
                                    size="24px"
                                    :value="1"
                                    color="positive"
                                    class="q-mb-sm"
                                >
                                    <div class="absolute-full flex flex-center">
                                        <q-badge
                                            color="white"
                                            text-color="secondary-dark"
                                            :label="`${$t('common.status.done')}: ${numberOfTasks.status[TaskStatus.DONE]} (100%)`"
                                        />
                                    </div>
                                </q-linear-progress>
                            </template>
                        </template>

                        <q-separator v-if="numberOfTasks.pending" class="q-mt-md q-mb-md" />

                        <template v-if="numberOfTasks.urgent">
                            <p>{{ $tc('views.dashboard.tasks.n-of-these-tasks-are-urgent', numberOfTasks.urgent) }}:</p>

                            <q-linear-progress
                                size="24px"
                                :value="numberOfTasks.dueDateStatus[TaskDueDateStatus.OVERDUE] / numberOfTasks.urgent"
                                color="negative"
                                class="q-mb-sm"
                            >
                                <div class="absolute-full flex flex-center">
                                    <q-badge
                                        color="white"
                                        text-color="secondary-dark"
                                        :label="`${$t('common.status.due-date-status.overdue')}: ${numberOfTasks.dueDateStatus[TaskDueDateStatus.OVERDUE]}`"
                                    />
                                </div>
                            </q-linear-progress>

                            <q-linear-progress
                                size="24px"
                                :value="numberOfTasks.dueDateStatus[TaskDueDateStatus.UPCOMING] / numberOfTasks.urgent"
                                color="warning"
                                class="q-mb-sm"
                            >
                                <div class="absolute-full flex flex-center">
                                    <q-badge
                                        color="white"
                                        text-color="secondary-dark"
                                    >
                                        <template v-if="numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY]">
                                            <template v-if="numberOfTasks.dueDateStatus[TaskDueDateStatus.UPCOMING] > numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY]">
                                                {{ $t('common.status.due-date-status.upcoming') }}: {{ numberOfTasks.dueDateStatus[TaskDueDateStatus.UPCOMING] }} ({{ $tc('views.dashboard.tasks.n-of-these-tasks-are-urgent--n-of-which-are-due-today', numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY]) }})
                                            </template>
                                            <template v-else>
                                                {{ $t('common.status.due-date-status.due-today') }}: {{ numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY] }}
                                            </template>
                                        </template>

                                        <template v-else>
                                            {{ $t('common.status.due-date-status.upcoming') }}: {{ numberOfTasks.dueDateStatus[TaskDueDateStatus.UPCOMING] }}
                                        </template>
                                    </q-badge>
                                </div>
                            </q-linear-progress>
                        </template>
                        <p v-else-if="numberOfTasks.pending" class="additional-info">{{ $tc('views.dashboard.tasks.n-of-these-tasks-are-urgent', 0) }}.</p>
                    </template>

                    <template v-slot:actions>
                        <base-button
                            :to="{ name: 'task-list' }"
                            :label="$t('common.task.all-tasks')"
                            primary-button
                            flat
                        />

                        <base-button
                            v-if="numberOfTasks.dueDateStatus[TaskDueDateStatus.OVERDUE]"
                            :to="{ name: 'task-list', query: { filterDueDateStatus: TaskDueDateStatus.OVERDUE } }"
                            :label="$tc('common.status.due-date-status.overdue', numberOfTasks.dueDateStatus[TaskDueDateStatus.OVERDUE])"
                            primary-button
                            flat
                        />

                        <base-button
                            v-if="numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY]"
                            :to="{ name: 'task-list', query: { filterDueDateStatus: TaskDueDateStatus.DUE_TODAY } }"
                            :label="$tc('common.status.due-date-status.due-today', numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY])"
                            primary-button
                            flat
                        />

                        <base-button
                            v-if="numberOfTasks.dueDateStatus[TaskDueDateStatus.UPCOMING] > numberOfTasks.dueDateStatus[TaskDueDateStatus.DUE_TODAY]"
                            :to="{ name: 'task-list', query: { filterDueDateStatus: TaskDueDateStatus.UPCOMING } }"
                            :label="$tc('common.status.due-date-status.upcoming', numberOfTasks.dueDateStatus[TaskDueDateStatus.UPCOMING])"
                            primary-button
                            flat
                        />
                    </template>
                </grid-card>
            </div>
        </div>
        <!--endregion-->
    </page-wrapper>
</template>

<script>
import { UserSettings } from '@/helpers/user'
import { GlobalReport } from '@/models/globalReport'
import kebabCase from 'lodash.kebabcase'
import { EventBus } from '@/event-bus'
import { add, addDays, differenceInCalendarDays, endOfDay, format, formatISO, parseISO, startOfDay } from 'date-fns'
import { formatDate, formatMonthDay, formatTime, getCurrentDateFnsLocale } from '@/helpers/date'
import { Task } from '@/models/task'
import { DashboardService } from '@/services/dashboard'
import { GlobalReportType, TaskStatus } from '@/enums/graphql'
import { TaskDueDateStatus, TaskStatistics } from '@/enums'
import TaskInfoList from '@/components/task/TaskInfoList.vue'
import { differenceInCalendarYears } from 'date-fns/fp'
import StatusBadge from '@/components/StatusBadge.vue'

export default {
    name: 'Dashboard',
    components: {
        StatusBadge,
        TaskInfoList,
    },
    meta () {
        return {
            title: this.$tc('common.term.dashboard', 1),
        }
    },
    data () {
        const store = this.$store.state
        const userConsultantId = store.user.consultant.id
        const userSettings = new UserSettings(this.$store.state.user)

        return {
            EventBus,
            userConsultantId,
            TaskStatus,
            TaskDueDateStatus,
            TaskStatistics,
            userSettings,
            tasksLoaded: false,
            tasksAssignedToUser: null,
            tasksCreatedByUser: null,
            numberOfTasks: {
                assignedToUser: 0,
                assignedToUserShown: 10,
                createdByUser: 0,
                createdByUserShown: 10,
                pending: 0,
                status: {
                    [TaskStatus.TODO]: 0,
                    [TaskStatus.IN_PROGRESS]: 0,
                    [TaskStatus.DONE]: 0,
                },
                urgent: 0,
                dueDateStatus: {
                    [TaskDueDateStatus.OVERDUE]: 0,
                    [TaskDueDateStatus.DUE_TODAY]: 0,
                    [TaskDueDateStatus.UPCOMING]: 0,
                    [TaskDueDateStatus.FUTURE]: 0,
                },
            },

            // GlobalReports
            contactsBirthdayReportIsLoading: true,
            contactsBirthdayReport: null,
            contractsExpirationReportIsLoading: true,
            contractsExpirationReport: null,
            contractsExpirationReportShowAutoRenewal: (userSettings.getItem('Dashboard.contractsExpirationReport.showAutoRenewal') ?? 'true') === 'true',
            contractsExpirationReportShowCancellation: (userSettings.getItem('Dashboard.contractsExpirationReport.showCancellation') ?? 'true') === 'true',
        }
    },
    watch: {
        'contractsExpirationReportShowAutoRenewal' (showAutoRenewal) {
            this.userSettings.setItem('Dashboard.contractsExpirationReport.showAutoRenewal', showAutoRenewal)
        },
        'contractsExpirationReportShowCancellation' (showCancellation) {
            this.userSettings.setItem('Dashboard.contractsExpirationReport.showCancellation', showCancellation)
        },
    },
    computed: {
        filteredContractsExpirationReportData () {
            return this.contractsExpirationReport?.data.filter(contractExpiration => {
                // Filter by auto_renewal: only include if showAutoRenewal is true OR contract has no auto_renewal (null)
                const autoRenewalFilter = this.contractsExpirationReportShowAutoRenewal || contractExpiration.auto_renewal === null

                // Filter by cancellation: only include if showCancellation is true OR contract has no cancellation (null)
                const cancellationFilter = this.contractsExpirationReportShowCancellation || contractExpiration.cancellation === null

                return autoRenewalFilter && cancellationFilter
            })
        },
        currentDateISO() {
            return formatISO(new Date(), { representation: 'date' })
        },
    },
    async created () {
        if (this.$can('Feature:tasks:core')) {
            const date = new Date()
            const overdueDate = +startOfDay(new Date()) - 1
            const dueDateUntil = endOfDay(addDays(date, Task.UPCOMING_DUE_DATE_AMOUNT_OF_DAYS))

            await DashboardService.all({
                filterUserId: this.$store.state.user.consultant.id,
                filterTasksStatisticsDueDateStatusDueTodayFrom: formatISO(startOfDay(date)),
                filterTasksStatisticsDueDateStatusDueTodayUntil: formatISO(endOfDay(date)),
                filterTasksStatisticsDueDateStatusOverdueUntil: formatISO(new Date(overdueDate)),
                filterTasksStatisticsDueDateStatusUpcomingFrom: formatISO(startOfDay(date)),
                filterTasksStatisticsDueDateStatusUpcomingUntil: formatISO(dueDateUntil),
                filterTasksStatisticsDueDateStatusFutureFrom: formatISO(+dueDateUntil + 1),
            }).then(result => {
                this.tasksLoaded = true
                this.tasksAssignedToUser = result.tasksAssigned.data
                this.tasksCreatedByUser = result.tasksCreated.data
                this.numberOfTasks = {
                    assignedToUser: result.tasksAssigned.paginatorInfo.total,
                    assignedToUserShown: result.tasksAssigned.paginatorInfo.count,
                    createdByUser: result.tasksCreated.paginatorInfo.total,
                    createdByUserShown: result.tasksCreated.paginatorInfo.count,
                    pending: result.tasksStatisticsStatusTodo.paginatorInfo.total + result.tasksStatisticsStatusInProgress.paginatorInfo.total,
                    status: {
                        [TaskStatus.TODO]: result.tasksStatisticsStatusTodo.paginatorInfo.total,
                        [TaskStatus.IN_PROGRESS]: result.tasksStatisticsStatusInProgress.paginatorInfo.total,
                        [TaskStatus.DONE]: result.tasksStatisticsStatusDone.paginatorInfo.total,
                    },
                    urgent: result.tasksStatisticsDueDateStatusOverdue.paginatorInfo.total + result.tasksStatisticsDueDateStatusUpcoming.paginatorInfo.total,
                    dueDateStatus: {
                        [TaskDueDateStatus.OVERDUE]: result.tasksStatisticsDueDateStatusOverdue.paginatorInfo.total,
                        [TaskDueDateStatus.DUE_TODAY]: result.tasksStatisticsDueDateStatusDueToday.paginatorInfo.total,
                        [TaskDueDateStatus.UPCOMING]: result.tasksStatisticsDueDateStatusUpcoming.paginatorInfo.total,
                        [TaskDueDateStatus.FUTURE]: result.tasksStatisticsDueDateStatusFuture.paginatorInfo.total,
                    },
                }
            })
        }

        GlobalReport.objects.all({ search: { type: GlobalReportType.CONTACTS_BIRTHDAY }}).then(response => {
            this.contactsBirthdayReport = response.at(0);
        }).finally(() => {
            this.contactsBirthdayReportIsLoading = false
        })

        GlobalReport.objects.all({ search: { type: GlobalReportType.CONTRACTS_EXPIRATION }}).then(response => {
            this.contractsExpirationReport = response.at(0);
        }).finally(() => {
            this.contractsExpirationReportIsLoading = false
        })
    },
    methods: {
        triggerCreateTask (targetObject) {
            EventBus.$emit('taskFormDialog:open', { targetObject })
        },
        kebabCase: function (text) {
            return kebabCase(text)
        },
        formatDate (...args) {
            return formatDate(...args)
        },
        formatMonthDay (...args) {
            return formatMonthDay(...args)
        },
        formatTime (...args) {
            return formatTime(...args)
        },
        daysUntilExpiration (date) {
            return differenceInCalendarDays(parseISO(date), parseISO(this.currentDateISO))
        },
        age (date) {
            return differenceInCalendarYears(parseISO(date), parseISO(this.currentDateISO))
        },
        daysUntilBirthday (date) {
            return differenceInCalendarDays(add(parseISO(date), { years: this.age(date) }), parseISO(this.currentDateISO))
        },
        birthdayTooltipText (name, birthday) {
            const translationKey = this.daysUntilBirthday(birthday) === 0 ? 'common.contact.birthday--contact-turns-n--today' : 'common.contact.birthday--contact-turns-n--on-date'
            const date = format(parseISO(birthday), 'd. MMMM', { locale: getCurrentDateFnsLocale() })
            const age = this.age(birthday)

            return this.$tc(translationKey, age, { name: name, date: date })
        },
    },
}
</script>
